import * as React from 'react';
import {useContext, useRef, useState} from 'react';
import {Divider, Form, InputRef, notification, Tag, TreeSelect, ConfigProvider} from 'antd';
import {useLocation, useNavigate, useParams} from "react-router-dom";
import { darkAntTheme } from '../../../theme/darkTheme';
import {
    ModalForm,
    ProFormDigit,
    ProFormGroup,
    ProFormInstance,
    ProFormList,
    ProFormSelect,
    ProFormText,
    ProFormTextArea
} from "@ant-design/pro-components";
import {PageLoading} from "@ant-design/pro-layout";
import axiosApiInstance from "../../../api/axiosClient";
import {GlobalStateContext} from "../../../context/GlobalContext";
import {useAtom, useAtomValue} from "jotai";
import {creatorCategoriesAtom, creatorTagsAtomById} from "./atoms";
import {queryClientAtom} from "jotai-tanstack-query";
import {ProFormTreeSelect} from "@ant-design/pro-form/lib";
import {generate} from "@ant-design/colors";
import {getPaletteColor} from "./utils";
import {getInitialSelectedValues, TagCategory} from "./CreatorViewEdit";
import ContactLinkField, { ContactLinkMigrationAlert } from "./ContactLinkField";

type Props = {};

const CreatorCreate = (props: Props) => {
        const [form] = Form.useForm<API.Creators.Creator>();
        const navigate = useNavigate();
        const routerParams = useParams();
        const [loading, setLoading] = useState<boolean>(false);
        const formRef = useRef<ProFormInstance>();
        const inputRef = useRef<InputRef>(null);
        const {
            employees
        } = useContext(GlobalStateContext);
        const [notifyManagerViaEmail, setNotifyManagerViaEmail] = useState<boolean>(true);
        const [{data: creatorsCategories, isLoading}] = useAtom(creatorCategoriesAtom);
        const queryClient = useAtomValue(queryClientAtom);
        const {SHOW_PARENT} = TreeSelect;

        const [{data: tagOptions}] = useAtom(creatorTagsAtomById(0));
        const [selectedValues, setSelectedValues] = useState<string[]>(getInitialSelectedValues(tagOptions));

    // Transform and sort tag options: rename "Specific" to "Content Category" and prioritize categories
    const transformedTagOptions = React.useMemo(() => {
        if (!tagOptions) return [];
        
        // Rename "Specific" to "Content Category" for display
        const transformed = tagOptions.map(option => ({
            ...option,
            label: option.label === 'Specific' ? 'Content Category' : option.label
        }));
        
        // Sort to show Prime Channel Category and Content Category first
        const sorted = [...transformed].sort((a, b) => {
            const priorityCategories = ['Prime Channel Category', 'Content Category','Channel Type'];
            const aIndex = priorityCategories.indexOf(a.label);
            const bIndex = priorityCategories.indexOf(b.label);
            
            if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
            if (aIndex !== -1) return -1;
            if (bIndex !== -1) return 1;
            return 0;
        });
        
        return sorted;
    }, [tagOptions]);
        // console.log('props', props.)

        const location = useLocation();
        const dataFromRouter = location.state?.data;
        if (dataFromRouter) {
            form.setFieldsValue(dataFromRouter)
        }

        const tagRender = (props: any) => {
            const {label, value, closable, onClose} = props;

            const findColor = (options: TagCategory[], value: string): string | undefined => {
                for (const option of options) {
                    if (option.value === value) return undefined;
                    const found = option.children.find(child => child.value === value);
                    if (found) return found.color;
                }
                return undefined;
            };

            const tagColor = findColor(transformedTagOptions, value);
            if (!tagColor) return null;
            const palette = tagColor ? generate(tagColor) : [];

            return (
                <Tag
                    color={tagColor}
                    closable={closable}
                    onClose={onClose}
                    style={{marginRight: 3, color: getPaletteColor(tagColor, palette)}} // Apply text color
                >
                    {label}
                </Tag>
            );
        };

        const handleChange = (values: string[]) => {
            let newSelectedValues = [...values];

            transformedTagOptions.forEach(category => {
                if (!category.allowMultiple) {
                    const selectedChildren = newSelectedValues.filter(value =>
                        category.children.some(child => child.value === value)
                    );
                    if (selectedChildren.length > 1) {
                        // Keep only the most recently selected child
                        const lastSelectedChild = selectedChildren.pop();
                        newSelectedValues = newSelectedValues.filter(value =>
                            !category.children.some(child => child.value === value)
                        );
                        if (lastSelectedChild !== undefined) {
                            newSelectedValues.push(lastSelectedChild);
                        }
                    }
                }
            });
            // console.log({newSelectedValues})
            setSelectedValues(newSelectedValues);
        };

        return (
            <ConfigProvider
                theme={
                    document.documentElement.classList.contains('dark-mode')
                        ? darkAntTheme
                        : {
                            token: {
                                colorBgBase: '#ffffff',
                                colorTextBase: '#000000',
                                colorBgContainer: '#ffffff',
                                colorBgElevated: '#fafafa',
                                colorBgLayout: '#f5f5f5',
                                borderRadius: 10,
                                borderRadiusLG: 12,
                                borderRadiusSM: 8,
                            },
                        }
                }
            >
                <ModalForm<API.Creators.Creator>
                    open={true}
                    width={"65%"}
                    form={form}
                    formRef={formRef}
                    autoFocusFirstInput
                    modalProps={{
                        destroyOnClose: true,
                        okText: 'Create',
                        okButtonProps: {loading: loading},
                        onCancel: () => navigate(`/creators/list`),

                    }}
                    onFinish={async formData => {
                        console.log(formData)
                        // return
                        return axiosApiInstance.post(`/api/creators/items`, formData, {
                            params: {
                                notify_manager_via_email: notifyManagerViaEmail
                            }
                        })
                            .then(() => {
                                notification.success({message: 'Created successfully'})
                                // queryClient.invalidateQueries({queryKey: ['creators', 'items']})
                                queryClient.invalidateQueries({queryKey: ['creators']})
                                navigate(`/creators/list`)
                                return true
                            })
                        // .then(()=>form.resetFields())
                    }}
                    validateMessages={{required: "${label} is required!"}}
                    // params={{notify_manager_via_email: notifyManagerViaEmail}}
                >

                {loading ? <PageLoading/> :
                    <>
                        <ContactLinkMigrationAlert />

                        <ProFormDigit
                            name="id"
                            hidden={true}
                        />

                        <ProFormGroup>
                            <ProFormText
                                width="lg"
                                name="creator"
                                label="Creator"
                                rules={[{required: true}]}
                            />

                            {/*<ProFormSelect*/}
                            {/*    name={'managed_by_employee_id'}*/}
                            {/*    label={'Managed By'}*/}
                            {/*    width="sm"*/}
                            {/*    initialValue={29}*/}
                            {/*    showSearch*/}
                            {/*    // mode={"multiple"}*/}
                            {/*    rules={[{required: true}]}*/}
                            {/*    options={employees?.map(i => ({label: i.full_name, value: i.id}))}*/}
                            {/*    help={*/}
                            {/*        form.getFieldValue('managed_by_employee_id') && employees?.filter(i => i.id == managed_by_employee_id)[0]?.email == '' ?*/}
                            {/*            <><WarningOutlined/> No*/}
                            {/*                email found</> :*/}
                            {/*            <div style={{*/}
                            {/*                display: 'flex',*/}
                            {/*                alignItems: 'center',*/}
                            {/*                justifyContent: 'space-between'*/}
                            {/*            }}> {employees?.filter(i => i.id == managed_by_employee_id)[0]?.email}*/}
                            {/*                <Tooltip title={'Notify via email'} placement={"bottom"}>*/}
                            {/*                    <Switch*/}
                            {/*                        // checkedChildren={'Notify'} unCheckedChildren={''}*/}
                            {/*                        checkedChildren={<BellOutlined/>}*/}
                            {/*                        unCheckedChildren={<BellOutlined/>}*/}
                            {/*                        // defaultChecked*/}
                            {/*                        checked={notifyManagerViaEmail}*/}
                            {/*                        onChange={setNotifyManagerViaEmail}*/}
                            {/*                        size={"small"}*/}
                            {/*                    />*/}
                            {/*                </Tooltip>*/}
                            {/*            </div>*/}
                            {/*    }*/}
                            {/*/>*/}
                        </ProFormGroup>

                        <ProFormGroup>
                            <ProFormSelect
                                width="xs"
                                // wrapperCol={{style: {width: 90}}}
                                name="main_platform_id"
                                label="Main Platform"
                                options={[{label: 'YouTube', value: 1}, {label: 'Instagram', value: 2}, {
                                    label: 'TikTok',
                                    value: 3
                                },]}
                                rules={[{required: true}]}
                            />

                            <ProFormTreeSelect
                                width={"lg"}
                                label={"Tags"}
                                name="tags"
                                normalize={(value, prevValue, allValues) => {
                                    // console.log({value, prevValue, allValues})
                                    return value.map(val => ({
                                        tag_category_id: parseInt(val.split(",")[0]),
                                        tag_category_value_id: parseInt(val.split(",")[1])
                                    }))
                                }}
                                fieldProps={{
                                    showCheckedStrategy: SHOW_PARENT,
                                    multiple: true,
                                    tagRender: (props) => tagRender(props),
                                    treeCheckable: true, // This will make selected items display as tags
                                    treeExpandAction: "click",
                                    value: selectedValues,
                                    onChange: handleChange,
                                    treeData: transformedTagOptions,
                                    filterTreeNode: (inputValue: string, treeNode: any) =>
                                        String(treeNode.label ?? treeNode.title ?? '').toLowerCase().includes(inputValue.toLowerCase()),
                                    // disabled: !props.editable,
                                }}
                            />
                        </ProFormGroup>

                        {/*<ProFormGroup>*/}

                        {/*    <ProFormDatePicker label={'Date of communication'} name={"date_of_communication"}*/}
                        {/*                       normalize={value => {*/}
                        {/*                           // console.log(value)*/}
                        {/*                           return new Date(value).toISOString()*/}
                        {/*                       }}*/}
                        {/*    />*/}

                        {/*    <ProFormSelect*/}
                        {/*        width="sm"*/}
                        {/*        name="communication_via_id"*/}
                        {/*        label="Communication via"*/}
                        {/*        // valueEnum={*/}
                        {/*        //     {*/}
                        {/*        //         1: {*/}
                        {/*        //             text: 'Email',*/}
                        {/*        //         },*/}
                        {/*        //     }}*/}
                        {/*        options={[{label: 'Email', value: 1}, {label: 'Phone', value: 2}, {*/}
                        {/*            label: 'Instagram',*/}
                        {/*            value: 3*/}
                        {/*        },]}*/}
                        {/*        rules={[{required: true}]}*/}

                        {/*    />*/}

                        {/*    <ProFormSelect*/}
                        {/*        width="sm"*/}
                        {/*        name="status_id"*/}
                        {/*        label="Status"*/}
                        {/*        options={[{label: 'Signed', value: 1}, {label: 'Pending', value: 2}, {*/}
                        {/*            label: 'Declined',*/}
                        {/*            value: 3*/}
                        {/*        },]}*/}
                        {/*        rules={[{required: true}]}*/}
                        {/*    />*/}

                        {/*</ProFormGroup>*/}

                        <ProFormGroup>
                            {/*<ProFormTextArea*/}
                            {/*    width="md"*/}
                            {/*    name="negotiation"*/}
                            {/*    label="Negotiation"*/}
                            {/*    // rules={[{required: true}]}*/}
                            {/*/>*/}

                            <ProFormTextArea
                                width="md"
                                name="reason"
                                label="Comments"
                                // rules={[{required: true}]}
                            />

                        </ProFormGroup>

                        <ProFormGroup title={"Linked Connections"} titleStyle={{font: "unset"}}>
                            <ContactLinkField editable={true} initialContacts={null} />
                        </ProFormGroup>

                        <Divider/>

                        <ProFormList name="accounts"
                                     label={'Social Media Accounts'}
                                     creatorButtonProps={{creatorButtonText: 'Add account'}}
                                     actionRender={(field, action, defaultActionDom) => [
                                         ...defaultActionDom,
                                     ]}
                        >
                            {(f, index, action) => {
                                return (
                                    <ProFormGroup>
                                        <ProFormDigit
                                            name="id"
                                            hidden={true}
                                        />

                                        <ProFormDigit
                                            name="creator_id"
                                            hidden={true}
                                        />

                                        <ProFormSelect
                                            // width="xs"
                                            wrapperCol={{style: {width: 70}}}
                                            name="platform_id"
                                            label="Platform"
                                            options={[{label: 'YouTube', value: 1}, {
                                                label: 'Instagram',
                                                value: 2
                                            }, {label: 'TikTok', value: 3},]}
                                            rules={[{required: true}]}
                                        />


                                        <ProFormText
                                            // width={"md"}
                                            wrapperCol={{style: {width: 430}}}
                                            label="Key"
                                            name="key"
                                        />

                                        <ProFormDigit
                                            name="following"
                                            label={'Following'}
                                            fieldProps={{precision: 0}}
                                            // width={"xs"}
                                            wrapperCol={{style: {width: 80}}}
                                        />

                                        {/*<ProFormSwitch*/}
                                        {/*    name="auto_fetch_followers"*/}
                                        {/*    label={'Auto Fetch Followers'}*/}
                                        {/*    // width={"xs"}*/}
                                        {/*    wrapperCol={{style: {width: 150}}}*/}
                                        {/*/>*/}

                                    </ProFormGroup>
                                );
                            }}
                        </ProFormList>

                        <Divider/>
                        {/*Pricings*/}
                        {/*YouTubeVideo
                            TikTokVideo
                            IGReel
                            IGPost
                            IGStory*/}
                        <ProFormGroup>

                            <ProFormDigit
                                name={["pricing", "youtube_video"]}
                                label="Price YouTube Video"
                                fieldProps={{precision: 0}}
                                // rules={[{required: true}]}
                            />

                            <ProFormDigit
                                name={["pricing", "tiktok_video"]}
                                label="Price TikTok Video"
                                fieldProps={{precision: 0}}
                                // rules={[{required: true}]}
                            />

                            <ProFormDigit
                                name={["pricing", "instagram_reel"]}
                                label="Price IG Reel"
                                fieldProps={{precision: 0}}
                                // rules={[{required: true}]}
                            />

                            <ProFormDigit
                                name={["pricing", "instagram_post"]}
                                label="Price IG Post"
                                fieldProps={{precision: 0}}
                                // rules={[{required: true}]}
                            />

                            <ProFormDigit
                                name={["pricing", "instagram_story"]}
                                label="Price IG Story"
                                fieldProps={{precision: 0}}
                                // rules={[{required: true}]}
                            />

                            <ProFormDigit
                                name={["pricing", "exclusivity"]}
                                label="Exclusivity"
                                fieldProps={{precision: 0}}
                                // rules={[{required: true}]}
                            />

                            <ProFormDigit
                                name={["pricing", "paid_ads_sponsorship"]}
                                label="Paid Ads/Sponsorship"
                                fieldProps={{precision: 0}}
                                // rules={[{required: true}]}
                            />

                            <ProFormDigit
                                name={["pricing", "collaboration"]}
                                label="Collaboration"
                                fieldProps={{precision: 0}}
                                // rules={[{required: true}]}
                            />


                        </ProFormGroup>

                    </>
                }

                </ModalForm>
            </ConfigProvider>
        );
    }
;

export default CreatorCreate;